import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getLoadingMessage } from "src/constants/loadingMessages";
import useUser from "src/hooks/useUser";
import { clearCurrentCustomer, setCustomerByName } from "src/slices/customers";
import { resetGlobals } from "src/slices/global";
import { saveSettings } from "src/slices/settings";
import {
  clearTaxesError,
  setCurrentLicense,
  setUpdatedTaxes,
} from "src/slices/taxes";
import { loadUserFromStorage, setIdentitySelected } from "src/slices/users";
import { useDispatch, useSelector } from "src/store";
import NotAuthorizedView from "src/views/errors/NotAuthorizedView";
import NotFoundView from "src/views/errors/NotFoundView";
import LoadingScreen from "./LoadingScreen";

/**
 * Determina si un módulo está habilitado basado en la ruta actual.
 * @param {string} pathname - Ruta actual
 * @param {Object} currentCustomer - Cliente actual
 * @param {Array} customerSections - Secciones disponibles del cliente
 * @returns {boolean} true si el módulo está habilitado
 */
const isModuleEnabled = (pathname, currentCustomer, customerSections) => {
  const normalizedPath =
    pathname && pathname !== "/" && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const customerBasePath = `/${currentCustomer?.shortname || ""}`;

  // Rutas globales siempre habilitadas (exactas)
  const globalExactPaths = [window.APP_HOME, "/logout", "/login", "/"];
  if (globalExactPaths.includes(normalizedPath)) {
    return true;
  }

  // Rutas globales siempre habilitadas (prefijo)
  const globalPrefixPaths = [
    "/monitor/appointments",
    "/app",
    "/AdminDashboard",
  ];
  if (
    globalPrefixPaths.some(
      (path) =>
        normalizedPath === path || normalizedPath.startsWith(`${path}/`),
    )
  ) {
    return true;
  }

  // Validar contexto de cliente
  if (!currentCustomer) {
    return false;
  }

  // Permitir siempre el home del cliente (/:customer) con o sin slash final
  if (normalizedPath === `/${currentCustomer.shortname}`) {
    return true;
  }

  if (!customerSections?.length) {
    return false;
  }

  // Ruta /geo siempre habilitada para cliente actual

  if (normalizedPath.startsWith(`${customerBasePath}/geo`)) {
    return true;
  }
  // Ruta /customer/ siempre habilitada para cliente actual
  if (normalizedPath.startsWith(`${customerBasePath}/customer`)) {
    return true;
  }

  // Ruta de configuración del customer habilitada para validaciones internas de acceso.
  if (normalizedPath.startsWith(`${customerBasePath}/config`)) {
    return true;
  }

  // Rutas de secciones del cliente o admin
  if (normalizedPath.startsWith(`${customerBasePath}/admin`)) {
    return true;
  }

  // Rutas administrativas corporativas (staff/users) se validan por permisos internos.
  if (normalizedPath.startsWith(`${customerBasePath}/corporateAdmin`)) {
    return true;
  }

  return (
    normalizedPath === customerBasePath ||
    customerSections.some((section) => {
      if (!section?.href) {
        return false;
      }

      const normalizedSectionHref = `/${`${section.href}`
        .replace(/^\/+/, "")
        .replace(/\/$/, "")}`;
      const fullSectionPath = `${customerBasePath}${normalizedSectionHref}`;

      return (
        normalizedPath === fullSectionPath ||
        normalizedPath.startsWith(`${fullSectionPath}/`)
      );
    })
  );
};

const shouldUseServiceRegistration = (currentCustomer) => {
  const pinHomeInscription =
    currentCustomer?.apps_config?.nets_service?.pin_home_inscription;
  return Boolean(pinHomeInscription);
};

const isUserRegistrationCompleted = (user) => {
  const value = user?.is_completed;

  if (value === true || value === 1) {
    return true;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }

  return false;
};

const AuthGuard = ({ children }) => {
  const { user, isAuthenticated, loaded, identitySelected, loginCustomer } =
    useSelector((state) => state.users);
  const dispatch = useDispatch();
  const {
    currentCustomer,
    customerSections,
    customersList,
    customersLoaded,
    customerError,
  } = useSelector((state) => state.customers);

  const { t } = useTranslation();
  const { identity } = useUser();
  const { motorError } = useSelector((state) => state.motor);
  const { taxesError } = useSelector((state) => state.taxes);
  const { customerName } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const registrationCustomerContext = currentCustomer || loginCustomer || null;
  const userRegistrationCompleted = isUserRegistrationCompleted(user);
  const isSimpleRegistrationPath =
    location.pathname === "/app/account/registration" ||
    location.pathname === "/app/account/registration/";
  const isServiceRegistrationPath =
    location.pathname === "/app/account/service-registration" ||
    location.pathname === "/app/account/service-registration/";
  const identityBootstrapTriedRef = useRef(false);
  const [identitySessionEndpointMissing, setIdentitySessionEndpointMissing] =
    useState(false);

  const [viewState, setViewState] = useState({
    checkingPerms: location.pathname.includes("/admin"),
    checkingIdentity: false,
    membership: false,
    redirectPath: null,
  });

  const identitySelectionRequired =
    isAuthenticated &&
    !identitySessionEndpointMissing &&
    !location.pathname.includes("/admin") &&
    !location.pathname.startsWith("/app/account/selectIdentity") &&
    !location.pathname.startsWith("/app/account/identities/new") &&
    !location.pathname.startsWith("/app/account/registration") &&
    !location.pathname.startsWith("/app/account/service-registration") &&
    !location.pathname.startsWith("/login") &&
    !location.pathname.includes("/logout") &&
    !identitySelected;

  // Calcular si el módulo está habilitado
  const moduleEnabled = useMemo(
    () => isModuleEnabled(location.pathname, currentCustomer, customerSections),
    [location.pathname, currentCustomer, customerSections],
  );

  // Detectar cliente por dominio personalizado
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname !== "arkadu.com" && !currentCustomer && customersList) {
      const customer = customersList.find((c) => c.custom_domain === hostname);
      if (customer) {
        dispatch(setCustomerByName({ shortname: customer.shortname }));
      }
    }
  }, [customersList, currentCustomer, dispatch]);

  // Sincronizar cliente desde params
  useEffect(() => {
    if (!customerName) {
      return;
    }
    if (!customersLoaded && !customersList?.length) {
      return;
    }
    if (!currentCustomer || currentCustomer.shortname !== customerName) {
      dispatch(setCustomerByName({ shortname: customerName }));
    }
  }, [customerName, currentCustomer, customersLoaded, customersList, dispatch]);

  // Limpiar contexto solo al entrar al home global /app (no en subrutas)
  useEffect(() => {
    if (location.pathname === "/app" || location.pathname === "/app/") {
      dispatch(saveSettings({ adminMode: false }));
      dispatch(clearCurrentCustomer({ licenses: null }));
      dispatch(setUpdatedTaxes({ licenses: null }));
      dispatch(setCurrentLicense(null));
      dispatch(resetGlobals());
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (!user) {
      dispatch(loadUserFromStorage());
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || identitySelected) {
      identityBootstrapTriedRef.current = false;
    }
  }, [isAuthenticated, identitySelected]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIdentitySessionEndpointMissing(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let mounted = true;

    const shouldBootstrapIdentity =
      isAuthenticated &&
      loaded &&
      !identitySelected &&
      !identitySessionEndpointMissing &&
      !location.pathname.includes("/admin") &&
      !location.pathname.startsWith("/login") &&
      !location.pathname.includes("/logout");

    if (!shouldBootstrapIdentity) {
      setViewState((prev) => ({
        ...prev,
        checkingIdentity: false,
      }));
      return undefined;
    }

    if (identityBootstrapTriedRef.current) {
      setViewState((prev) => ({
        ...prev,
        checkingIdentity: false,
      }));
      return undefined;
    }

    identityBootstrapTriedRef.current = true;

    setViewState((prev) => ({
      ...prev,
      checkingIdentity: true,
    }));

    const bootstrapIdentity = async () => {
      try {
        const activeIdentity = await identity.getSessionIdentity();
        if (!mounted) {
          return;
        }
        if (activeIdentity) {
          dispatch(setIdentitySelected(activeIdentity));
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        if (
          error?.status === 404 ||
          error?.code === "identity_session_endpoint_missing"
        ) {
          setIdentitySessionEndpointMissing(true);
          enqueueSnackbar(
            t(
              "La validación de identidad no está disponible en este ambiente. Se continuará sin bloqueo por identidad.",
            ),
            { variant: "warning" },
          );
        }
      } finally {
        if (mounted) {
          setViewState((prev) => ({
            ...prev,
            checkingIdentity: false,
          }));
        }
      }
    };

    bootstrapIdentity();

    return () => {
      mounted = false;
    };
  }, [
    dispatch,
    enqueueSnackbar,
    identitySelected,
    identitySessionEndpointMissing,
    isAuthenticated,
    loaded,
    location.pathname,
    t,
  ]);

  useEffect(() => {
    if (motorError) {
      enqueueSnackbar(motorError, { variant: "error" });
    }
  }, [motorError, enqueueSnackbar]);

  useEffect(() => {
    if (taxesError) {
      enqueueSnackbar(taxesError, { variant: "error" });
      dispatch(clearTaxesError());
    }
  }, [taxesError, enqueueSnackbar, dispatch]);

  const checkUserMembership = useCallback(() => {
    if (!currentCustomer || !user?.member_set) {
      setViewState((prev) => ({
        ...prev,
        membership: false,
        checkingPerms: false,
      }));
      return false;
    }

    const membership = user.member_set.find(
      (m) => m.customer_id === currentCustomer.id,
    );
    const isMember = !!membership;

    setViewState((prev) => ({
      ...prev,
      membership: isMember,
      checkingPerms: false,
    }));

    if (!isMember) {
      dispatch(saveSettings({ adminMode: false }));
    }

    return isMember;
  }, [currentCustomer, user, dispatch]);

  // Guardar ruta siguiente para login
  useEffect(() => {
    if (!isAuthenticated && loaded) {
      if (!window.location.pathname.endsWith("login")) {
        localStorage.setItem("next", window.location.pathname);
      }
    }
  }, [isAuthenticated, loaded]);

  // Verificar membresía para rutas admin
  useEffect(() => {
    if (location.pathname.includes("/admin")) {
      if (user && currentCustomer) {
        setViewState((prev) => ({
          ...prev,
          checkingPerms: true,
        }));
        checkUserMembership();
      } else {
        setViewState((prev) => ({
          ...prev,
          checkingPerms: !loaded,
        }));
      }
    } else {
      setViewState((prev) => ({
        ...prev,
        checkingPerms: false,
        redirectPath: null,
      }));
    }
  }, [currentCustomer, user, location.pathname, loaded, checkUserMembership]);

  // Redirigir si sin membresía en rutas admin (excepto corporate)
  useEffect(() => {
    if (
      location.pathname.includes("/admin") &&
      !viewState.checkingPerms &&
      !viewState.membership &&
      !location.pathname.includes("/corporate/admin/") &&
      !location.pathname.includes("/corporate/procedure/admin")
    ) {
      const newPath = location.pathname.replace("/admin", "");
      setViewState((prev) => ({
        ...prev,
        redirectPath: newPath,
      }));
      return;
    }

    setViewState((prev) => ({
      ...prev,
      redirectPath: null,
    }));
  }, [location.pathname, viewState.membership, viewState.checkingPerms]);

  if (
    user &&
    !userRegistrationCompleted &&
    !isSimpleRegistrationPath &&
    !isServiceRegistrationPath
  ) {
    const targetPath = shouldUseServiceRegistration(registrationCustomerContext)
      ? "/app/account/service-registration"
      : "/app/account/registration";
    return <Navigate to={targetPath} replace />;
  }

  if (
    user &&
    !userRegistrationCompleted &&
    isSimpleRegistrationPath &&
    shouldUseServiceRegistration(registrationCustomerContext)
  ) {
    return <Navigate to="/app/account/service-registration" replace />;
  }

  if (
    customerName &&
    !currentCustomer &&
    !location.pathname.startsWith("/app/")
  ) {
    if (customersLoaded) {
      return (
        <NotFoundView
          message={
            customerError || t("No se encontró el cliente solicitado en la URL")
          }
        />
      );
    }

    return (
      <LoadingScreen
        message={getLoadingMessage("loadingCustomerContext")}
        suppressWhenGlobalLoading={false}
      />
    );
  }

  if (!moduleEnabled && currentCustomer) {
    return (
      <NotAuthorizedView
        message={t("El cliente no tiene habilitado este módulo")}
      />
    );
  }

  if (viewState.checkingPerms || viewState.checkingIdentity || !loaded) {
    return (
      <LoadingScreen
        message={t("verificando permisos")}
        suppressWhenGlobalLoading={false}
      />
    );
  }

  if (!isAuthenticated && loaded) {
    return <Navigate to={customerName ? `/${customerName}/login` : "/login"} />;
  }

  if (identitySelectionRequired) {
    return <Navigate to="/app/account/selectIdentity/" replace />;
  }

  if (viewState.redirectPath) {
    return <Navigate to={viewState.redirectPath} />;
  }

  if (location.pathname.includes("/admin") && !viewState.membership) {
    if (
      location.pathname.includes("/corporate/admin/") ||
      location.pathname.includes("/corporate/procedure/admin")
    ) {
      return <NotAuthorizedView />;
    }
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
